import React, { useCallback, useMemo, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import styled from 'styled-components';
import { dayjs } from '@/utils/dayjs';
import {
  MONTH_DATE_STRING_FORMAT,
  MONTH_SEPARATION_HEADER_PADDING_PX,
  MONTH_WIDTH_PX,
  SWIMLANE_LABEL_PANE_WIDTH_PX,
} from '../../constants';
import { useCalendarDisplayOffsetOperations } from '../../hooks/use-calendar-display-offset-operations';
import { getListingLivePartialEventsByMonth } from '../../utils/calendar-event-generators/listing-live-events';
import {
  getCurrentMonthStartDateString,
  getMonthStartDateString,
} from '../../utils/dates';
import { CalendarMonth } from '../calendar-month';
import { CalendarMonthLabelSidebar } from '../calendar-month-label-sidebar';
import { MonthNavButtons } from './month-nav-buttons';

const MonthList = styled(VariableSizeList)``;

const CalendarContainer = styled.div`
  position: relative;
`;

const addPaymentPeriods = (pp1, pp2) => {
  if (!pp1) return pp2;
  if (!pp2) return pp1;

  return {
    ...pp1,
    actualTot: pp1.actualTot + pp2.actualTot,
    numDaysOccupied: Math.max(pp1.numDaysOccupied, pp2.numDaysOccupied),
  };
};

function getTOTPaymentsByMonthStartDate(licenseData) {
  const allLicensePayments =
    licenseData?.flatMap(({ totPayments }) => totPayments || []) || [];

  return allLicensePayments.reduce(
    (acc, paymentPeriod) => ({
      ...acc,
      [paymentPeriod.startDate]: addPaymentPeriods(
        acc[paymentPeriod.startDate],
        paymentPeriod
      ),
    }),
    {}
  );
}

function getReviewsByMonthStart(listings) {
  const allReviews = listings.flatMap((l) => l.reviews).filter(Boolean);
  return allReviews.reduce((acc, review) => {
    const monthStartDate = getMonthStartDateString(review.createdTs);
    return {
      ...acc,
      [monthStartDate]: [...(acc[monthStartDate] || []), review],
    };
  }, {});
}

function populateReviews(months, listings) {
  const reviewsByMonthStart = getReviewsByMonthStart(listings);
  return months.map((m) => ({
    ...m,
    reviews: reviewsByMonthStart[m.date] || [],
  }));
}

function populateCheckIns(dataPoints) {
  let prevBBDate = null;

  let checkInId = 0;
  return (dataPoints || []).map((month) => ({
    ...month,
    calendarActivity: month.calendarActivity.map((activity) => {
      const isCheckIn =
        activity.status === 'booked' && activity.bbDate !== prevBBDate;

      prevBBDate = activity.bbDate;
      if (isCheckIn) checkInId++;

      return {
        ...activity,
        isCheckIn,
        checkInId,
      };
    }),
  }));
}

function populateMonthsWithReviewsAndCheckIns(propertyDataPoints, listings) {
  const months = populateCheckIns(propertyDataPoints);
  const unsortedMonths = populateReviews(months, listings)
    .filter(({ date }) => Boolean(date))
    .map((monthData) => ({
      ...monthData,
      _dayjsDate: dayjs(monthData.date, MONTH_DATE_STRING_FORMAT),
    }));

  return (
    unsortedMonths
      // eslint-disable-next-line no-underscore-dangle
      .sort((a, b) => a._dayjsDate.diff(b._dayjsDate))
  );
}

function stringMin(s1, s2) {
  if (!s1) return s2;
  if (!s2) return s1;

  if (s1 < s2) return s1;
  return s2;
}

function padMonths(earliestDate, latestDate, months) {
  const result = [];

  const monthsByStartDate = months.reduce(
    (acc, month) => ({
      ...acc,
      // eslint-disable-next-line no-underscore-dangle
      [month._dayjsDate.set('date', 1).format(MONTH_DATE_STRING_FORMAT)]: month,
    }),
    {}
  );

  for (
    let monthDate = earliestDate;
    monthDate.diff(latestDate) <= 0;
    monthDate = monthDate.add(1, 'month')
  ) {
    const populatedMonth =
      monthsByStartDate[monthDate.format(MONTH_DATE_STRING_FORMAT)];

    if (populatedMonth) {
      result.push(populatedMonth);
      continue;
    }

    result.push({
      date: monthDate.format(MONTH_DATE_STRING_FORMAT),
      calendarActivity: [],
      occupied: [],
      reviews: [],
    });
  }

  return result;
}

export function MonthlyCalendar({
  style,
  className,
  property,
  licenseData,
  showTOTCollectionMetrics,
}) {
  const listings = useMemo(() => property.listings || [], [property.listings]);

  const totPaymentsByMonthStartDate = useMemo(
    () => getTOTPaymentsByMonthStartDate(licenseData),
    [licenseData]
  );

  const months = useMemo(() => {
    const totMonthDates = Object.keys(totPaymentsByMonthStartDate).sort();
    const earliestTOTMonthDateString = totMonthDates[0];
    const populatedMonths = populateMonthsWithReviewsAndCheckIns(
      property.dataPoints,
      listings
    );
    const earliestPopulatedMonthDateString = populatedMonths[0]?.date;

    const earliestMonthDateStringToDisplay = stringMin(
      earliestPopulatedMonthDateString,
      earliestTOTMonthDateString
    );

    const latestMonthDateToDisplay = dayjs().set('date', 1);

    const earliestMonthDateToDisplay = !earliestMonthDateStringToDisplay
      ? latestMonthDateToDisplay.subtract(1, 'month')
      : dayjs(earliestMonthDateStringToDisplay, MONTH_DATE_STRING_FORMAT).set(
          'date',
          1
        );

    return padMonths(
      earliestMonthDateToDisplay,
      latestMonthDateToDisplay,
      populatedMonths
    );
  }, [property?.dataPoints, listings]);

  const listingLiveEventsByMonth = useMemo(
    () => getListingLivePartialEventsByMonth(listings),
    [listings]
  );

  const monthListRef = useRef();

  const {
    scrollToMonth,
    onGoToPrevMonth,
    onGoToNextMonth,
    getScrollOffsetForMonth,
  } = useCalendarDisplayOffsetOperations({
    monthListRef,
    monthWidthPx: MONTH_WIDTH_PX,
    monthSeparationPx: MONTH_SEPARATION_HEADER_PADDING_PX,
  });

  const currentMonthStartDateString = getCurrentMonthStartDateString();
  const currentMonthIndex = useMemo(
    () => months.findIndex((m) => m.date === currentMonthStartDateString),
    [currentMonthStartDateString, months]
  );

  const onGoToCurrentMonth = useCallback(() => {
    scrollToMonth(currentMonthIndex);
  }, [scrollToMonth, currentMonthIndex]);

  return (
    <CalendarContainer style={style} className={className}>
      <AutoSizer>
        {({ height, width }) => (
          <MonthList
            itemCount={months.length}
            itemData={{
              months,
              listingLiveEventsByMonth,
              totPaymentsByMonthStartDate,
              showTOTCollectionMetrics,
            }}
            itemSize={(index) =>
              (index === 0 ? SWIMLANE_LABEL_PANE_WIDTH_PX : 0) + MONTH_WIDTH_PX
            }
            height={height}
            initialScrollOffset={
              currentMonthIndex && getScrollOffsetForMonth(currentMonthIndex)
            }
            layout={'horizontal'}
            width={width}
            ref={monthListRef}
          >
            {CalendarMonth}
          </MonthList>
        )}
      </AutoSizer>
      <CalendarMonthLabelSidebar
        onGoToCurrentMonth={onGoToCurrentMonth}
        listings={listings}
      />
      <MonthNavButtons
        onGoToPrevMonth={onGoToPrevMonth}
        onGoToNextMonth={onGoToNextMonth}
      />
    </CalendarContainer>
  );
}
